import { useCallback, useEffect, useState } from 'react'
import { useMotionValue, useInView, animate, type MotionValue } from 'motion/react'
import type { RefObject } from 'react'
import { BEATS } from './beats'

/** One full 0..1 pass, including the raw hold below. */
const DEFAULT_DURATION_MS = 6000

/**
 * How long the `raw` beat holds before detection starts.
 *
 * `raw` is a hold, not an animation: the transcript sits unchanged so the
 * viewer registers what "before" looks like. Played linearly it would consume
 * its full share of the timeline — over a second of nothing moving — so it
 * gets this fixed, short slice and the remaining beats share the rest.
 */
const RAW_HOLD_MS = 500

/**
 * How long the finished board rests before the loop restarts. Without it the
 * cards land and are immediately yanked back to a blank transcript, which
 * reads as a glitch rather than a replay.
 */
const END_HOLD_MS = 2500

export type AutoplayControls = {
  progress: MotionValue<number>
  /** False when the viewer has paused the loop. */
  isPlaying: boolean
  togglePlaying: () => void
}

/**
 * Loops the 0..1 sequence for as long as the target is in view.
 *
 * Because this animates indefinitely, the returned `togglePlaying` is not a
 * nicety — WCAG 2.2.2 requires a stop mechanism for motion that runs beyond
 * five seconds. Callers must expose it.
 */
export function useAutoplayProgress(
  target: RefObject<HTMLElement>,
  opts: { durationMs?: number } = {}
): AutoplayControls {
  const progress = useMotionValue(0)
  const durationMs = opts.durationMs ?? DEFAULT_DURATION_MS
  const [isPlaying, setIsPlaying] = useState(true)
  const togglePlaying = useCallback(() => setIsPlaying((p) => !p), [])

  // Deliberately not `once: true`. The loop should run while the section is
  // on screen and stop when it is not, rather than burn frames off-screen.
  const inView = useInView(target, { amount: 0.4 })

  useEffect(() => {
    if (!inView || !isPlaying) return

    const [, rawEnd] = BEATS.raw
    const cycleMs = durationMs + END_HOLD_MS

    progress.set(0)
    const controls = animate(progress, [0, rawEnd, 1, 1], {
      duration: cycleMs / 1000,
      times: [0, RAW_HOLD_MS / cycleMs, durationMs / cycleMs, 1],
      ease: 'linear',
      repeat: Infinity,
    })
    return () => controls.stop()
  }, [inView, isPlaying, progress, durationMs])

  return { progress, isPlaying, togglePlaying }
}
